
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from '@/utils/formatters';

interface TicketOptimizationProps {
  optimization: {
    purchasedTickets: Array<{ price: number; capital: number; quantity: number }>;
    remainingAmount: number;
    totalCapital: number;
    totalSpent: number;
  };
}

export const TicketOptimizer: React.FC<TicketOptimizationProps> = ({ optimization }) => {
  return (
    <div className="bg-card border border-border rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-lg">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-2">
          🎫 Tối Ưu Hóa Mua Vé
        </h2>
        <p className="text-sm sm:text-base text-muted-foreground">Phân bổ vé thông minh để tối đa hóa vốn</p>
      </div>

      <div className="space-y-4 sm:space-y-6">
        {/* Mobile Card View - Hidden on larger screens */}
        <div className="block sm:hidden space-y-3">
          {optimization.purchasedTickets.map((ticket, index) => (
            <div key={index} className="bg-secondary border border-border p-4 rounded-xl">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-muted-foreground mb-1">Giá Vé</div>
                  <div className="font-semibold text-primary">{formatCurrency(ticket.price)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground mb-1">Số Lượng</div>
                  <div className="font-bold text-tradebrand-blue">{ticket.quantity}</div>
                </div>
                <div>
                  <div className="text-muted-foreground mb-1">Vốn Được Cấp</div>
                  <div className="text-tradebrand-green">{formatCurrency(ticket.capital)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground mb-1">Tổng Vốn</div>
                  <div className="font-bold text-primary">{formatCurrency(ticket.quantity * ticket.capital)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table View - Hidden on mobile */}
        <div className="hidden sm:block overflow-x-auto">
          <div className="overflow-hidden rounded-2xl bg-secondary border border-border">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-secondary/50">
                  <TableHead className="text-primary font-bold">Giá Vé</TableHead>
                  <TableHead className="text-tradebrand-green font-bold">Vốn Được Cấp</TableHead>
                  <TableHead className="text-tradebrand-blue font-bold">Số Lượng</TableHead>
                  <TableHead className="text-foreground font-bold">Tổng Vốn</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {optimization.purchasedTickets.map((ticket, index) => (
                  <TableRow key={index} className="border-border hover:bg-secondary/50 transition-colors duration-200">
                    <TableCell className="font-semibold text-foreground">
                      {formatCurrency(ticket.price)}
                    </TableCell>
                    <TableCell className="text-tradebrand-green">{formatCurrency(ticket.capital)}</TableCell>
                    <TableCell className="text-tradebrand-blue font-bold">{ticket.quantity}</TableCell>
                    <TableCell className="font-bold text-primary">
                      {formatCurrency(ticket.quantity * ticket.capital)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Stats Grid - Mobile Optimized */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-secondary border border-border p-3 sm:p-4 rounded-xl sm:rounded-2xl">
            <div className="text-xs sm:text-sm text-muted-foreground mb-1">Tổng Chi Phí</div>
            <div className="text-lg sm:text-xl font-bold text-foreground">{formatCurrency(optimization.totalSpent)}</div>
          </div>
          <div className="bg-secondary border border-border p-3 sm:p-4 rounded-xl sm:rounded-2xl">
            <div className="text-xs sm:text-sm text-muted-foreground mb-1">Số Dư</div>
            <div className="text-lg sm:text-xl font-bold text-foreground">{formatCurrency(optimization.remainingAmount)}</div>
          </div>
          <div className="bg-secondary border border-border p-3 sm:p-4 rounded-xl sm:rounded-2xl">
            <div className="text-xs sm:text-sm text-muted-foreground mb-1">Tổng Vốn Được Cấp</div>
            <div className="text-lg sm:text-xl font-bold text-tradebrand-green">{formatCurrency(optimization.totalCapital)}</div>
          </div>
          <div className="bg-secondary border border-border p-3 sm:p-4 rounded-xl sm:rounded-2xl">
            <div className="text-xs sm:text-sm text-muted-foreground mb-1">Tỷ Lệ Đòn Bẩy</div>
            <div className="text-lg sm:text-xl font-bold text-primary">
              {(optimization.totalCapital / optimization.totalSpent).toFixed(1)}x
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
